import { useRouter } from "next/router"
import { useState, useEffect } from "react"
import { NextSeo } from "next-seo"
import image, { PRODUCT_PHOTOGRAMME } from "lacinetek-common/src/images"

import { useTranslation } from "lib/i18n/translation"
import Gtm from "lib/gtm"
import { FETCH_MY_PLAYLISTS } from "lib/graphql/queries/admin-queries"
import { getFilms } from "lib/film-fetcher"
import playlistSlug from "lib/playlists/slug"
import { useCountry } from "lib/country"
import { useLanguages } from "lib/languages"

import PlaylistPublic from "components/playlist/public-page"

import Page404 from "pages/[lang]/404"

const Playlist = ({ playlist }) => {
	const { c } = useTranslation("common")
	const { current } = useLanguages()
	const { country, asPath } = useCountry()
	const [products, setProducts] = useState([])

	useEffect(() => {
		const timeout = setTimeout(async () => {
			const ids = playlist.products.items.map(product => product.id)
			const products = await getFilms(ids, current, country.isoCode)
			setProducts(products)
		})

		return () => {
			clearTimeout(timeout)
		}
	}, [current, country])

	if (!playlist) {
		return <Page404 />
	}

	const router = useRouter()
	const url = router ? router.asPath : null

	return (
		<>
			<Gtm pageType="Playlist" pagePath={url} pageTitle={playlist.name} />
			<NextSeo
				title={playlist.name}
				description={playlist.note}
				canonical={asPath}
				openGraph={{
					type: 'website',
					url: asPath,
					title: playlist.name,
					description: playlist.note,
					images: [
						{
							url: image(playlist.products.items[0], PRODUCT_PHOTOGRAMME),
							width: 800,
							height: 600,
							alt: playlist.name,
						}
					],
				}}
			/>
			<PlaylistPublic title={c("playlists")} playlist={playlist} products={products} />
		</>
	)
}

Playlist.getInitialProps = async (ctx) => {
	const [_, id] = ctx.query.slug.match(/^([0-9]*)_.*/) || []
	if (!id) {
		ctx.res.statusCode = 400

		return {
			id,
			playlist: null,
			playlists: [],
			products: []
		}
	}

	const { data: { playlists: { items } } } = await ctx.apolloClient.query({
		query: FETCH_MY_PLAYLISTS,
		context: { clientName: "adminKinow" },
		variables: { includeIds: [id] }
	})

	const noPlaylist = items.length === 0
	if (noPlaylist) {
		ctx.res.statusCode = 404

		return {
			id,
			playlist: null,
			playlists: [],
			products: []
		}
	}

	const playlist = items[0]
	const slug = playlistSlug(playlist)

	if (slug !== ctx.query.slug) {
		const parts = ctx.req.originalUrl.split("?")[0].split("/")
		const countryAndLocale = parts[1]

		ctx.res.writeHead(302, {
			Location: `/${countryAndLocale}/playlists/${slug}`
		})
		ctx.res.end()

		return {
			id,
			playlist: null,
			playlists: [],
			products: []
		}
	}

	return {
		playlist
	}
}

export default Playlist
